import {
  formatDialect,
  mysql,
  plsql,
  postgresql,
  redshift,
  singlestoredb,
  sql,
  transactsql,
  type DialectOptions,
  type KeywordCase,
  type ParamTypes,
} from 'sql-formatter';
import { DB_TYPE } from '@/common/define/common.define';
import type { DBType } from '../types';

interface SqlFormatterConfig {
  dialect: DialectOptions;
  paramTypes?: ParamTypes;
}

interface DialectConfig {
  dialect: DialectOptions;
  extraOperators?: string[];
}

/**
 * sql-formatter의 createDialect가 객체 참조 기반으로 캐싱하므로 모듈 레벨에서 1회 선언해 캐시 히트를 보장하기 위해 선언
 */
const DIALECT_CONFIG: Partial<Record<Exclude<DBType, null>, DialectConfig>> = {
  [DB_TYPE.POSTGRESQL]: { dialect: postgresql, extraOperators: ['=='] },
  [DB_TYPE.MYSQL]: { dialect: mysql },
  [DB_TYPE.CUBRID]: { dialect: mysql },
  [DB_TYPE.ORACLE]: { dialect: plsql },
  [DB_TYPE.TIBERO]: { dialect: plsql },
  [DB_TYPE.ALTIBASE]: { dialect: plsql },
  [DB_TYPE.SQLSERVER]: { dialect: transactsql },
  [DB_TYPE.REDSHIFT]: { dialect: redshift },
  [DB_TYPE.SINGLESTORE]: { dialect: singlestoredb },
};

const buildDialectOptions = (config: DialectConfig): DialectOptions => {
  const { dialect, extraOperators } = config;

  if (!extraOperators?.length) {
    return dialect;
  }

  return {
    ...dialect,
    tokenizerOptions: {
      ...dialect.tokenizerOptions,
      operators: [...(dialect.tokenizerOptions.operators ?? []), ...extraOperators],
    },
  };
};

const MERGED_DIALECT_OPTION: Partial<Record<Exclude<DBType, null>, DialectOptions>> =
  Object.fromEntries(
    Object.entries(DIALECT_CONFIG).map(([dbType, config]) => [dbType, buildDialectOptions(config)]),
  ) as Partial<Record<Exclude<DBType, null>, DialectOptions>>;

const EXTRA_DIALECT_OPTION: Partial<Record<Exclude<DBType, null>, Partial<ParamTypes>>> = {
  [DB_TYPE.TIBERO]: { quoted: [':'] },
  [DB_TYPE.ALTIBASE]: {
    positional: true,
    named: ['$'],
  },
  [DB_TYPE.REDSHIFT]: { positional: true },
  [DB_TYPE.SINGLESTORE]: { positional: true },
};

/**
 * 기본/추가 paramTypes 배열 값을 중복 없이 병합
 */
const mergeParamTypeValues = <T>({
  base,
  extra,
}: {
  base?: readonly T[];
  extra?: readonly T[];
}) => {
  if (!base?.length && !extra?.length) {
    return undefined;
  }

  return [...new Set([...(base ?? []), ...(extra ?? [])])];
};

/**
 * dialect 기본 paramTypes에 DB 타입별 확장 규칙 병합
 */
const mergeParamTypes = ({
  base,
  extra,
}: {
  base?: ParamTypes;
  extra?: Partial<ParamTypes>;
}): ParamTypes | undefined => {
  if (!base && !extra) {
    return undefined;
  }

  return {
    positional: extra?.positional ?? base?.positional,
    numbered: mergeParamTypeValues({ base: base?.numbered, extra: extra?.numbered }),
    named: mergeParamTypeValues({ base: base?.named, extra: extra?.named }),
    quoted: mergeParamTypeValues({ base: base?.quoted, extra: extra?.quoted }),
    custom: [...(base?.custom ?? []), ...(extra?.custom ?? [])],
  };
};

/**
 * DB 타입에 맞는 sql-formatter dialect와 paramTypes 설정 반환
 */
export const getSqlFormatConfigByDbType = (dbType: DBType): SqlFormatterConfig => {
  const dialect = dbType ? (MERGED_DIALECT_OPTION[dbType] ?? sql) : sql;

  const paramTypes = mergeParamTypes({
    base: dialect.tokenizerOptions.paramTypes,
    extra: dbType ? EXTRA_DIALECT_OPTION[dbType] : undefined,
  });

  return {
    dialect,
    paramTypes,
  };
};

/**
 * SQL 텍스트에서 주석, 줄바꿈, 탭, 중복 공백 제거
 */
export const trimSqlText = (sqlTextData: string) => {
  if (!sqlTextData) return '';
  return sqlTextData
    .replace(/(--).*/g, '')
    .replace(/\/\*[^+].*?\*\//gs, '')
    .replace(/\n/g, ' ')
    .replace(/\t/g, ' ')
    .replace(/ +/g, ' ');
};

/**
 * SQL 텍스트 포맷팅
 */
export const formatSqlText = (
  sqlText: string | null,
  dbTypeStr: string,
  option?: { keywordCase: KeywordCase },
) => {
  if (!sqlText) {
    return '';
  }
  const dbType = DB_TYPE[dbTypeStr.toUpperCase()] ?? null;
  const { dialect, paramTypes } = getSqlFormatConfigByDbType(dbType);
  let formattingSql = '';
  try {
    formattingSql = formatDialect(`${sqlText}`, {
      dialect,
      tabWidth: 2,
      keywordCase: option?.keywordCase ?? 'upper',
      linesBetweenQueries: 2,
      paramTypes,
    });
  } catch (e) {
    formattingSql = '';
    console.error(e);
  }
  return formattingSql;
};

/**
 * SQL 텍스트의 placeholder(=?)를 default value로 치환
 */
export const replaceSqlDefaultValue = ({
  sqlText,
  placeholder = '?',
  defaultValue = "'1'",
}: {
  sqlText: string | null;
  placeholder?: string;
  defaultValue?: string;
}) => {
  if (!sqlText) {
    return sqlText;
  }
  return sqlText.replaceAll(new RegExp(`@\\${placeholder}|\\${placeholder}`, 'gm'), defaultValue);
};
